// ─── Gli avvisi ai soci: i sette gesti, e la frase che ne esce ───────────────
//
// 🧭 **IL GESTIONALE SA, IL BOT DICE.** Questa riga governa tutto il file. Il
// bot non calcola niente per conto suo, non tiene un archivio parallelo, non
// deduce da ciò che ha detto prima: riceve e ripete.
//
// ⚖️ Da lì discende la decisione meno ovvia della Fase 7: **la frase la compone
// il gestionale, e viaggia dentro la riga di coda** (`payload.testo`). Le
// bocche possono essere due — il bot che ritira dal ponte, e il consegnatore
// che chiama Telegram da qui — e due bocche che si compongono la frase da sé
// sono due verità sulla stessa cosa. 📌 *Due bocche che dicono la stessa frase
// non sono due verità; due bocche che se la scrivono da sole sì.*
//
// 🕰️ E la frase si scrive nell'ISTANTE DEL FATTO, non a consegna. Un avviso
// racconta **una cosa successa**, non lo stato di adesso: se la partita nel
// frattempo si è mossa un'altra volta, il messaggio di ieri deve continuare a
// dire ciò che era vero ieri — o si consegna una storia riscritta.
import { z } from 'zod';
import { dataSchema, oraSchema } from './circolo.js';
import { oraPiuMinuti } from './calendario.js';
/**
 * ⚠️ SONO SETTE, ed erano cinque fino al 10/09/2026: `durata` e `maestro` sono
 * arrivati dopo. L'elenco vive in tre posti che devono restare d'accordo — il
 * `CHECK` della tabella, questa riga e il bot — e il `CHECK` è quello che
 * comanda: una migrazione si vede, una costante no.
 *
 * ⛔ Restano MUTI — e sono la strada per provare senza disturbare nessuno —
 * **nota**, **descrizione**, **importo a carico** e l'attivazione o
 * disattivazione di un socio. 📌 *Un elenco di ciò che è innocuo invecchia
 * peggio di uno di ciò che è pericoloso: il primo si legge per stare
 * tranquilli.*
 */
export const GESTI = [
    'aggiunto',
    'tolto',
    'annullata',
    'spostata',
    'formazione',
    'durata',
    'maestro',
    /**
     * 💡 **La risposta della segreteria** a un suggerimento o a un problema del
     * socio (24/09/2026). È un gesto nuovo perché è un FATTO nuovo, e non
     * riguarda una partita: niente `dove`, e la frase la scrive la segreteria
     * (`messaggio`). ⚖️ Il bot non scarta i gesti che non conosce — legge
     * `text` e lo manda — quindi può arrivare prima di lui.
     */
    'risposta',
    /**
     * 🌙 **La caparra della fascia serale** (25/09/2026): è tornata nel
     * borsellino, è rimasta al circolo, il credito sta per scadere, o la
     * Segreteria ha tenuto un campo e aspetta il pagamento. Come `risposta`, la
     * frase è già scritta (`messaggio`); `depositoId` c'è quando il bot deve
     * mettere sotto il bottone per pagare.
     */
    'caparra',
    /**
     * 🎁 **Il premio a chi organizza** (26/09/2026, voce 33): i crediti arrivati a
     * fine partita, e quelli che stanno per scadere. Frase già scritta
     * (`messaggio`), come `risposta` e `caparra`.
     */
    'premio',
    /**
     * 🔁 **Cambia organizzatore** (26/09/2026, fase 3 della caparra): la
     * richiesta a chi dovrebbe prendere la partita, e com'è finita a chi l'ha
     * chiesta. La frase è già scritta (`messaggio`); `cambioId` c'è quando il
     * bot deve mettere sotto «Sì, organizzo io» e «No, grazie».
     */
    'organizzatore',
    /**
     * ⏱️ **Arrivo in ritardo** (27/09/2026, mockup «Scrivi alla Segreteria» ④):
     * un socio lo dice all'assistente, e i compagni di quella partita lo sanno.
     * Frase già scritta (`messaggio`). Non va alla Segreteria: glielo dicono gli
     * amici al campo.
     */
    'ritardo',
    /**
     * 🔁 **Il rinnovo del campo fisso** (27/09/2026, voce 47): una settimana
     * prima dell'ultima partita, e due giorni prima se il socio non ha risposto.
     * Frase già scritta (`messaggio`); `serieId` e `rinnovoDa` dicono al bot
     * quale bottone mettere sotto — «🔁 Rinnova il campo fisso», o «✉️ Chiedi il
     * rinnovo alla Segreteria» per i fissi che il bot non sa rifare uguali.
     */
    'fisso',
];
export const gestoSchema = z.enum(GESTI);
export const ETICHETTE_GESTO = {
    aggiunto: 'Aggiunto in campo',
    tolto: 'Tolto dalla partita',
    annullata: 'Partita annullata',
    spostata: 'Partita spostata',
    formazione: 'Formazione cambiata',
    durata: 'Durata cambiata',
    maestro: 'Maestro cambiato',
    risposta: 'Risposta della segreteria',
    caparra: 'Acconto',
    premio: 'Premio a chi organizza',
    organizzatore: 'Cambio d’organizzatore',
    ritardo: 'Arrivo in ritardo',
    fisso: 'Rinnovo del campo fisso',
};
/** Dove e quando si gioca. Il nome del campo, non il suo identificativo: lo legge una persona. */
export const coordinateSchema = z.object({
    courtName: z.string(),
    date: dataSchema,
    time: oraSchema,
    durationMinutes: z.number().int(),
});
/**
 * Il fatto, per esteso.
 *
 * 🚨 `da` C'È SOLO DOVE HA SENSO, e la regola non è estetica: **chi è stato
 * tolto durante uno spostamento riceve le coordinate VECCHIE e nessun «da»**.
 * Del posto nuovo non deve sapere niente, o lo si manda a giocare a una
 * partita che non è più sua.
 *
 * 🧾 `requestedBy` è **la ricevuta**, ed è ciò che sostituisce il salto del
 * primo dell'elenco: non si annuncia a una persona ciò che ha fatto lei stessa.
 * La convenzione vecchia — «il primo è l'organizzatore, salta lui» — perdeva un
 * avviso su quattro, e su una partita di una persona sola li perdeva tutti.
 */
export const payloadAvvisoSchema = z.object({
    /**
     * Dove si va a giocare **adesso** (o dove si andava, per un annullo e per chi è stato tolto).
     * ⚠️ Manca solo in `risposta`, che non parla di una partita.
     */
    dove: coordinateSchema.optional(),
    /** 💡 Solo `risposta`, `caparra` e `premio`: la frase intera, già composta dal gestionale. */
    messaggio: z.string().optional(),
    /** 🌙 Solo `caparra`: la caparra da pagare, quando il bot deve mettere il bottone. */
    depositoId: z.uuid().optional(),
    /** 🔁 Solo `caparra` (fase 3, R7): il rinnovo del campo fisso, che dal bot non si annulla. */
    rinnovo: z.boolean().optional(),
    /** 🆕 27/09/2026 — il campo vuoto: sotto l'avviso, nel bot, «🎾 Prenota un campo». */
    prenotaDiNuovo: z.boolean().optional(),
    /** 🆕 Voce 50 — la partita del campo vuoto: sotto l'avviso, «✉️ Eravamo in campo». */
    ricontrolla: z.uuid().optional(),
    /** 🆕 Voce 50 — il blocco: sotto l'avviso, «✉️ Scrivi alla Segreteria». */
    scriviAllaSegreteria: z.boolean().optional(),
    /** 🔁 Solo `fisso` (voce 47): la serie da rinnovare, e se si rinnova dal bot o in Segreteria. */
    serieId: z.uuid().optional(),
    rinnovoDa: z.enum(['bot', 'segreteria']).optional(),
    /**
     * 🔁 Solo `organizzatore` (fase 3, R5): la richiesta a cui rispondere, se di
     * sera serve la caparra, se prende il posto di chi chiede; e sull'esito, la
     * richiesta rifiutata (per il bottone «Chiedo a un altro»).
     */
    cambioId: z.uuid().optional(),
    conCaparra: z.boolean().optional(),
    alPostoSuo: z.boolean().optional(),
    cambioRifiutato: z.uuid().optional(),
    /** Da dove si veniva: solo `spostata`, `durata`, `maestro`. */
    da: coordinateSchema.partial().optional(),
    tipo: z.string().optional(),
    titolo: z.string().nullable().optional(),
    maestro: z.string().nullable().optional(),
    /** Chi è entrato e chi è uscito, per il gesto `formazione`. */
    entrati: z.array(z.string()).optional(),
    usciti: z.array(z.string()).optional(),
    /**
     * 🎓⭐⭐ **QUANTE VOLTE DEL CORSO RIGUARDA QUESTO FATTO — 18/09/2026, tappa ⑤.**
     *
     * ⚖️ **Perché non è nato un ottavo gesto**, che era la strada più ovvia e
     * sarebbe stata la più cara. Un'iscrizione a un corso **è** un «aggiunto»:
     * quella persona è stata messa in campo. Ciò che cambia non è il fatto, è
     * **quanto è grande** — sei lunedì invece di un martedì.
     * ⛔ E un gesto nuovo si paga fuori di qui: il bot **scarta i gesti che non
     *   conosce**, quindi andrebbe insegnato a lui per primo, poi migrato il
     *   `CHECK`, poi acceso il gestionale — e nella finestra fra i due deploy
     *   quell'avviso non lo direbbe **nessuno**. Per una frase diversa è un prezzo
     *   che non ha senso pagare.
     * 📌 *Un gesto nuovo si inventa quando cambia il FATTO, non quando cambia la
     *    frase che lo racconta.*
     *
     * ⭐ E serve perché senza di lui l'unica alternativa era **un avviso per data**:
     * iscrivere quattro persone a un corso di sei volte avrebbe fatto squillare
     * ventiquattro volte quattro telefoni, nello stesso istante, per dire quattro
     * cose.
     */
    volte: z.number().int().optional(),
    motivo: z.string().nullable().optional(),
    /** Il socio che ha chiesto la modifica, quando a chiederla è stato un socio dal bot. */
    requestedBy: z.uuid().nullable().optional(),
    /**
     * 🌙 **Arriva anche di notte** (26/09/2026, dal committente: *«gli avvisi della
     * Segreteria possono arrivare sempre»*). Vero sui fatti nati da un gesto di
     * una persona del circolo — la Segreteria, il maestro — e sulla risposta a
     * un pagamento del socio. ⛔ Falso (assente) sugli avvisi automatici, come
     * il credito che scade: un calcolo non sveglia nessuno a mezzanotte. E sui
     * gesti dei soci dal bot, che di notte aspettano la mattina come prima.
     */
    ancheDiNotte: z.boolean().optional(),
    /** ✉️ Sul gesto `risposta`: il messaggio a cui la Segreteria risponde, per il bottone «Rispondi alla Segreteria». */
    feedbackId: z.uuid().optional(),
    /** La frase, composta nell'istante del fatto. È questa che arriva sul telefono. */
    testo: z.string(),
});
/**
 * Gli stati di una riga di coda. Sono **quattro** e non cinque: manca
 * apposta un «in consegna».
 *
 * ⚖️ Un quinto stato sembrerebbe più preciso e sarebbe una bugia in più da
 * mantenere: a tenere una riga fuori dalle mani di un'altra replica è il
 * **fermo** (`claimed_at`), che scade da sé. Uno stato «in consegna» scritto da
 * un processo che poi muore resta lì per sempre, e nessuno sa se quel messaggio
 * è partito o no.
 */
export const STATI_AVVISO = ['pending', 'delivered', 'failed', 'skipped'];
export const statoAvvisoSchema = z.enum(STATI_AVVISO);
export const ETICHETTE_STATO_AVVISO = {
    pending: 'da mandare',
    delivered: 'consegnato',
    failed: 'non consegnato',
    skipped: 'non da mandare',
};
export const avvisoSchema = z.object({
    id: z.uuid(),
    bookingId: z.uuid().nullable(),
    memberId: z.uuid(),
    memberName: z.string(),
    gesture: gestoSchema,
    payload: payloadAvvisoSchema,
    status: statoAvvisoSchema,
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    createdAt: z.string(),
    deliveredAt: z.string().nullable(),
    /** Quando la coda lo ridà al bot. Conta solo mentre è `pending`. */
    nextAttemptAt: z.string().nullable(),
});
export const elencoAvvisiSchema = z.object({
    items: z.array(avvisoSchema),
    nextCursor: z.string().nullable(),
});
export const domandaAvvisiSchema = z.object({
    status: statoAvvisoSchema.optional(),
    gesture: gestoSchema.optional(),
    memberId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
    periodo: z.enum(['oggi', 'settimana', 'tutti']).default('tutti'),
    /** Un pezzo del nome del socio. */
    q: z.string().trim().max(80).optional(),
    /** Il motivo esatto di un «non consegnato», per vedere solo quelli. */
    motivo: z.string().max(500).optional(),
});
// ── La pagina «Avvisi ai soci», in parole semplici (25/09/2026) ─────────────
//
// 🎨 Dal mockup approvato `avvisi-ai-soci-operativa-mockup.html`: la pagina
// dice prima se il bot passa, poi cosa c'è da mandare, e i non consegnati li
// raggruppa per motivo. Le frasi nascono qui, e non nella schermata, perché
// sono regole: si provano senza un browser.
export const PERIODI_AVVISI = ['oggi', 'settimana', 'tutti'];
export const ETICHETTE_PERIODO_AVVISI = {
    oggi: 'Oggi',
    settimana: 'Ultimi 7 giorni',
    tutti: 'Tutti',
};
/**
 * L'attesa prima di riprovare, per numero di tentativi già fatti: 1′, 5′, 15′,
 * 1 h, 3 h. Dopo, si smette. Sta qui perché la schermata dice «tentativo 3 di
 * 5», e il 5 deve essere lo stesso numero con cui la coda smette.
 */
export const ATTESE_AVVISO_MINUTI = [1, 5, 15, 60, 180];
export const TENTATIVI_AVVISO = ATTESE_AVVISO_MINUTI.length;
/**
 * Oltre questi minuti senza un passaggio, il bot «non passa».
 * ⚖️ Il bot guarda la coda ogni minuto: cinque giri mancati non sono un
 * ritardo, sono un bot fermo.
 */
export const BOT_FERMO_MINUTI = 5;
/**
 * Le ore in cui il bot non scrive a nessuno, e quindi non ritira la coda.
 * ⚠️ È la stessa notte del bot (`NOTTE_DA`/`NOTTE_A` in `partita/scadenza.ts`
 * di miopadel-bot): se una delle due cambia, cambia anche l'altra, o di notte
 * questa pagina direbbe «il bot è fermo» a un bot che dorme.
 */
export const NOTTE_DEL_BOT = { da: 22, a: 8 };
export const quadroAvvisiSchema = z.object({
    /** `true` ⇒ gli avvisi li manda il gestionale da sé, col suo gettone. */
    consegnatoreArmato: z.boolean(),
    mancano: z.array(z.string()),
    /** I conteggi del periodo scelto. */
    perStato: z.record(statoAvvisoSchema, z.number().int()),
    /** L'ultima volta che il bot ha ritirato la coda, anche vuota. */
    ultimoRitiro: z.string().nullable(),
    /** I non consegnati del periodo, raggruppati per motivo, i più numerosi prima. */
    motivi: z.array(z.object({ motivo: z.string(), quante: z.number().int() })),
});
const ALLA_ROMANA = { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit' };
/** «16:08», all'ora del circolo. */
export function oraDelCircolo(iso) {
    return new Date(iso).toLocaleTimeString('it-IT', ALLA_ROMANA);
}
function oraRomana(adesso) {
    return Number(new Intl.DateTimeFormat('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', hourCycle: 'h23' }).format(adesso));
}
/** La riga in cima alla pagina: il bot passa, o no? */
export function statoDelBot(q, adesso) {
    if (q.consegnatoreArmato) {
        return {
            tono: 'gestionale',
            titolo: 'Gli avvisi li manda il gestionale',
            dettaglio: 'guarda la coda ogni 15 secondi.',
        };
    }
    const ora = oraRomana(adesso);
    const notte = ora >= NOTTE_DEL_BOT.da || ora < NOTTE_DEL_BOT.a;
    const minuti = q.ultimoRitiro ? (adesso.getTime() - new Date(q.ultimoRitiro).getTime()) / 60_000 : Infinity;
    if (minuti <= BOT_FERMO_MINUTI) {
        return {
            tono: 'ok',
            titolo: `Il bot dei soci è passato alle ${oraDelCircolo(q.ultimoRitiro)}`,
            dettaglio: 'e ritira gli avvisi ogni minuto.',
        };
    }
    if (notte) {
        return {
            tono: 'notte',
            titolo: `Di notte il bot non manda avvisi`,
            dettaglio: `dalle ${NOTTE_DEL_BOT.da} alle ${NOTTE_DEL_BOT.a}. Quelli in coda partono al mattino.`,
        };
    }
    const da = Number.isFinite(minuti)
        ? `da ${Math.floor(minuti)} minuti`
        : 'da quando questa pagina tiene il conto';
    return {
        tono: 'fermo',
        titolo: `Il bot dei soci non passa ${da}`,
        dettaglio: 'Gli avvisi restano in coda e partono quando torna: non si perdono.',
    };
}
/** Lo stato di un avviso, in una parola e in una frase. */
export function fraseDelloStato(a) {
    switch (a.status) {
        case 'delivered':
            return { stato: '✓ Consegnato', perche: a.deliveredAt ? `alle ${oraDelCircolo(a.deliveredAt)}` : '' };
        case 'failed':
            return { stato: '✕ Non consegnato', perche: a.lastError ?? '' };
        case 'skipped':
            return { stato: 'Non da mandare', perche: a.lastError ?? 'L’ha chiesto il socio stesso.' };
        case 'pending': {
            if (a.attempts === 0)
                return { stato: '⏳ In attesa', perche: 'Parte al prossimo passaggio del bot.' };
            const prossimo = Math.min(a.attempts + 1, TENTATIVI_AVVISO);
            const quando = a.nextAttemptAt ? `alle ${oraDelCircolo(a.nextAttemptAt)} ` : '';
            const motivo = a.lastError ? `${a.lastError.replace(/[.\s]+$/u, '')}. ` : '';
            return {
                stato: '⏳ In attesa',
                perche: `${motivo}Il bot riprova ${quando}(tentativo ${prossimo} di ${TENTATIVI_AVVISO}).`,
            };
        }
    }
}
/**
 * Cosa vuol dire un motivo di «non consegnato», per chi deve fare qualcosa.
 * Le frasi sono quelle che il bot scrive (`gestionale/avvisi.ts` e `avvisi/giro.ts`
 * di miopadel-bot) e quella del consegnatore interno.
 */
export function spiegaMotivo(motivo) {
    if (/^Non usa il bot dei soci/.test(motivo)) {
        return 'Questi soci non sono nel bot, quindi non ricevono niente. Dalla scheda del socio trovi il link per entrare.';
    }
    if (/Telegram non consegna/.test(motivo))
        return 'Il socio ha bloccato il bot o ha cancellato la chat.';
    if (/Telegram non ha risposto/.test(motivo)) {
        return 'Telegram non ha risposto per cinque volte di fila. Si può riprovare.';
    }
    if (/stava usando il bot/.test(motivo)) {
        return 'Il socio usava il bot a ogni tentativo. Si può riprovare.';
    }
    if (/nessuna chat Telegram in rubrica/.test(motivo))
        return 'Il socio non ha una chat nella Rubrica Telegram.';
    return null;
}
// ── La rubrica ──────────────────────────────────────────────────────────────
/**
 * ⚠️ NON È UNA LISTA PIATTA DI RAGGIUNGIBILI: è una rubrica **per
 * proprietario**. La chiave è *(chi ha in rubrica, chi)*, e lo stesso nome
 * compare una volta per ogni persona che ce l'ha — è normale.
 *
 * 📏 Il 14/09/2026 una sonda che guardava due colonne sole ha fatto sembrare
 * doppioni sette righe per cinque persone, a un passo dal cancellarne due —
 * cioè dal togliere un contatto dalla rubrica di qualcuno.
 * ⇒ Ne discende la regola di consegna: **il destinatario è una CHAT, non una
 *   riga**. Un socio che sta nella rubrica di tre persone riceve un messaggio
 *   solo.
 */
export const contattoRubricaSchema = z.object({
    ownerMemberId: z.uuid(),
    ownerName: z.string(),
    memberId: z.uuid(),
    memberName: z.string(),
    chatId: z.string().nullable(),
    label: z.string().nullable(),
    addedAt: z.string(),
});
export const elencoRubricaSchema = z.object({ items: z.array(contattoRubricaSchema) });
export const chatIdSchema = z
    .string()
    .trim()
    .regex(/^-?[0-9]{5,20}$/, 'Il codice della chat Telegram è un numero, per esempio 1256773674');
export const creaContattoSchema = z.object({
    ownerMemberId: z.uuid(),
    memberId: z.uuid(),
    chatId: chatIdSchema.nullable().default(null),
    label: z.string().trim().max(120).nullable().default(null),
});
// ── Chi, fra lo staff, riceve le notifiche di servizio ───────────────────────
export const destinatarioStaffSchema = z.object({
    userId: z.uuid(),
    userName: z.string(),
    email: z.string().nullable(),
    chatId: z.string().nullable(),
    active: z.boolean(),
});
export const elencoDestinatariSchema = z.object({ items: z.array(destinatarioStaffSchema) });
export const scriviDestinatariSchema = z.object({
    items: z
        .array(z.object({
        userId: z.uuid(),
        chatId: chatIdSchema.nullable().default(null),
        active: z.boolean().default(true),
    }))
        .max(50),
});
// ── La frase ────────────────────────────────────────────────────────────────
const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MESI = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
];
/**
 * `2026-09-21` → «lunedì 21 settembre».
 *
 * ⚠️ Il giorno della settimana si calcola in **UTC** da una data nuda, e non è
 * una svista: `2026-09-21` non è un istante, è un giorno. Passandolo per il
 * fuso del processo, a Roma d'estate diventerebbe la sera prima.
 */
export function giornoPerEsteso(data) {
    const d = new Date(`${data}T12:00:00Z`);
    return `${GIORNI[d.getUTCDay()]} ${d.getUTCDate()} ${MESI[d.getUTCMonth()]}`;
}
/** «lunedì 21 settembre alle 19:30 (fino alle 21:00), campo 4». */
export function quando(c) {
    return `${giornoPerEsteso(c.date)} alle ${c.time} (fino alle ${oraPiuMinuti(c.time, c.durationMinutes)}), ${c.courtName}`;
}
/**
 * La frase che arriva sul telefono del socio.
 *
 * 🚨 **UNO SPOSTAMENTO NON È UN ANNULLO**, ed è il difetto da cui nasce il
 * criterio di questa fase: due volte, sul sistema di oggi, uno spostamento è
 * arrivato al committente come una disdetta. Qui `spostata` dice **dove si va
 * adesso** e da dove si veniva, in una frase sola.
 *
 * ⛔ E non nomina mai un pezzo interno: qui dentro non si scrive un codice, un
 * identificativo, un nome di tabella. Chi legge è un socio.
 */
/**
 * 🚨⭐⭐ **COME SI CHIAMA LA COSA DI CUI SI STA PARLANDO — 18/09/2026.**
 *
 * 🗣️ È la sua regola applicata alle parole: *«gli avvisi se devono arrivare
 * devono arrivare **corretti fino in fondo**»*.
 *
 * 📏 **Misurato sul banco durante la tappa ⑤**: accorciando un corso, ai tre
 * iscritti arrivava *«Partita annullata: lunedì 2 novembre…»*. Non era una
 * partita: era il loro corso. La frase diceva il vero sul fatto e il falso sulla
 * cosa — e chi la legge cerca una partita che non ha mai prenotato.
 *
 * ⚖️ Il ripiego è **«la prenotazione»** e non «la partita»: un tipo nuovo che
 * nessuno avesse dichiarato qui darebbe una frase generica e vera, invece di una
 * precisa e sbagliata. ⚠️ E il ripiego vale anche quando `tipo` non arriva
 * affatto — succede sulle righe di coda scritte prima di oggi.
 */
const LA_COSA = {
    partita: 'la partita',
    lezione: 'la lezione',
    allenamento: 'l’allenamento',
    /**
     * 🎓🚨 **«questa volta del corso», e NON «il corso»** — corretto scrivendolo,
     * dopo averlo letto sul banco.
     *
     * ⚖️ Un gesto arriva qui per **una prenotazione**, e la prenotazione di un
     * corso è **una delle sue date**. *«Il corso è stato annullato»* detto per il
     * lunedì 5 ottobre farebbe credere a sei persone che il corso è finito, e
     * nessuno di loro si presenterebbe il 12.
     * ⇒ Il gesto che riguarda **tutto** il corso è un altro (l'iscrizione e la
     *   disiscrizione, che hanno la loro frase, ⇒ il ramo `volte` qui sotto).
     * 📌 *Un messaggio che allarga un fatto è una bugia anche quando ogni sua
     *    parola è vera.*
     */
    corso: 'questa volta del corso',
    torneo: 'il torneo',
    manutenzione: 'la chiusura del campo',
};
function laCosa(tipo) {
    return LA_COSA[tipo ?? ''] ?? 'la prenotazione';
}
/** La stessa parola con l'iniziale grande, per cominciare una frase. */
function LaCosa(tipo) {
    const c = laCosa(tipo);
    return c[0].toUpperCase() + c.slice(1);
}
/**
 * «nella partita», «nel corso» (fino al 28/09/2026 era «dalla partita», per «sei stato tolto»).
 *
 * ⚖️ Si RICAVA dall'articolo invece di tenere una seconda tabella: due elenchi
 * da tenere d'accordo a mano divergono il giorno in cui nasce un tipo nuovo, e a
 * divergere sarebbe quello che nessuno rilegge.
 */
/**
 * `'o'` o `'a'`: la desinenza che il participio deve prendere.
 *
 * 🚨 **Serve, e non è pedanteria da grammatico**: senza, la frase di un corso
 * annullato esce *«Il corso è stata annullata»*, che è la cosa che fa capire a
 * chi legge che gliel'ha scritta una macchina — proprio sul messaggio che deve
 * essere creduto. 📏 Misurata addosso scrivendo questa cura: la prima stesura
 * cambiava il nome e lasciava il participio, e le prove sono cadute lì.
 * ⚖️ Si ricava dall'**articolo**, come `dalla` qui sotto: una tabella in più da
 * tenere d'accordo a mano divergerebbe al primo tipo nuovo.
 */
function desinenza(tipo) {
    return laCosa(tipo).startsWith('il ') ? 'o' : 'a';
}
function nella(tipo) {
    const c = laCosa(tipo);
    if (c.startsWith('la '))
        return `nella ${c.slice(3)}`;
    if (c.startsWith('il '))
        return `nel ${c.slice(3)}`;
    return `in ${c}`;
}
export function componiAvviso(gesto, p) {
    // 💡 La risposta della segreteria non parla di una partita: la frase c'è già.
    if (gesto === 'risposta' ||
        gesto === 'caparra' ||
        gesto === 'premio' ||
        gesto === 'organizzatore' ||
        gesto === 'ritardo' ||
        gesto === 'fisso')
        return p.messaggio ?? '';
    if (!p.dove)
        throw new Error(`L'avviso «${gesto}» non dice dove si gioca.`);
    const dove = quando(p.dove);
    switch (gesto) {
        case 'aggiunto': {
            /*
             * 🎓 Un'iscrizione a un corso è lo stesso gesto — quella persona è in
             * campo — ma non è UNA volta, e dirle «sei in campo lunedì» quando i
             * lunedì sono sei sarebbe vero e incompleto. Le coordinate sono quelle
             * della **prima** volta: è da lì che comincia.
             */
            if (p.tipo === 'corso' && p.volte !== undefined && p.volte > 1) {
                const nome = p.titolo ? ` «${p.titolo}»` : '';
                return `Sei iscritto al corso${nome}: ${p.volte} volte, si comincia ${dove}.`;
            }
            return `Sei in campo: ${dove}.`;
        }
        case 'tolto': {
            // Le coordinate sono quelle VECCHIE, e non c'è nessun «da»: se questa
            // partita si è mossa, del posto nuovo non deve sapere niente.
            if (p.tipo === 'corso' && p.volte !== undefined && p.volte > 1) {
                const nome = p.titolo ? ` «${p.titolo}»` : '';
                return `Il corso${nome} per te si chiude qui: le ${p.volte} volte che restavano sono state liberate, dalla prima ${dove}. Se vuoi riprenderlo, la Segreteria ti aiuta volentieri.`;
            }
            // 🌞 28/09/2026: in positivo, senza un genere, e con la strada per giocare lo stesso.
            const altre = p.tipo === 'partita' || p.tipo === undefined
                ? ' Se ti va di giocare, in «🙋 Partite che cercano giocatori» trovi le altre.'
                : ' Per un’altra data, la Segreteria ti aiuta volentieri.';
            return `Il tuo posto ${nella(p.tipo)} è stato liberato: ${dove}.${altre}`;
        }
        case 'annullata': {
            /*
             * 🎓⭐⭐ **IL CORSO INTERO, o una sua volta sola: due fatti diversi** — ed
             * è lo stesso ramo che `aggiunto`, `tolto` e `durata` hanno già. Qui
             * mancava, ed era l'ultimo dei quattro: annullando le volte che restavano
             * di un corso da sei, la frase diceva *«questa volta del corso è stata
             * annullata: lunedì 5 ottobre»* — vera sul lunedì, e **falsa sul corso**,
             * perché chi la legge continua ad aspettare il lunedì dopo.
             * 📌 *Una frase giusta su una riga può essere una bugia sul fatto, quando
             *    il fatto è più grande della riga.*
             *
             * ⛔ Non è un gesto nuovo, ed è deliberato: `volte` è un campo del
             *    payload, e il bot che non lo conosce legge `testo` e basta (⇒ la
             *    riga sopra: *«un gesto nuovo si inventa quando cambia il FATTO»*).
             */
            if (p.tipo === 'corso' && p.volte !== undefined && p.volte > 1) {
                const nome = p.titolo ? ` «${p.titolo}»` : '';
                const che = `Il corso${nome} è stato annullato: le ${p.volte} volte che restavano ` +
                    `sono tolte dal calendario, dalla prima ${dove}`;
                const perRiprendere = ' Per un altro corso, la Segreteria ti aiuta volentieri.';
                return (p.motivo ? `${che}. Motivo: ${p.motivo}.` : `${che}.`) + perRiprendere;
            }
            const d = desinenza(p.tipo);
            const che = `${LaCosa(p.tipo)} è stat${d} annullat${d}`;
            // 🌞 28/09/2026: sempre la strada per giocare lo stesso.
            const strada = p.tipo === 'partita' || p.tipo === undefined
                ? ' Se ti va di giocare, puoi prenotare un altro campo da «🎾 Prenota un campo».'
                : ' Per un’altra data, la Segreteria ti aiuta volentieri.';
            return (p.motivo ? `${che}: ${dove}. Motivo: ${p.motivo}.` : `${che}: ${dove}.`) + strada;
        }
        case 'spostata': {
            const prima = p.da?.date && p.da?.time && p.da?.courtName && p.da?.durationMinutes !== undefined
                ? quando(p.da)
                : null;
            const mossa = `${LaCosa(p.tipo)} si è spostat${desinenza(p.tipo)}`;
            return prima ? `${mossa}: adesso è ${dove}. Prima era ${prima}.` : `${mossa}: adesso è ${dove}.`;
        }
        case 'durata': {
            /*
             * 🎓⭐⭐ **IL CORSO INTERO, o una sua volta sola: due fatti diversi.**
             * 18/09/2026, 🗣️ *«mi devi far modificare la durata di una lezione o di un
             * corso»*. Cambiando la durata di **tutte** le volte che restano parte un
             * avviso **per persona** (non uno per data, ⇒ `corso.ts`), e quella
             * persona deve capire che non è cambiato solo il lunedì che ha davanti:
             * `LaCosa('corso')` dice «questa volta del corso», che qui sarebbe falso.
             */
            const daA = p.da?.durationMinutes !== undefined
                ? `da ${p.da.durationMinutes} a ${p.dove.durationMinutes} minuti`
                : `adesso è di ${p.dove.durationMinutes} minuti`;
            if (p.tipo === 'corso' && p.volte !== undefined && p.volte > 1) {
                const nome = p.titolo ? ` «${p.titolo}»` : '';
                return `Il corso${nome} cambia durata: ${daA}, su tutte e ${p.volte} le volte che restano. Dalla prima ${dove}.`;
            }
            return p.da?.durationMinutes !== undefined
                ? `${LaCosa(p.tipo)} cambia durata: ${daA}. Adesso è ${dove}.`
                : `${LaCosa(p.tipo)} cambia durata: adesso è ${dove}.`;
        }
        case 'maestro':
            // 🆕 19c: un allenamento è maschile, e il messaggio si accorda.
            if (p.tipo === 'allenamento') {
                return p.maestro
                    ? `L’allenamento lo tiene ${p.maestro}: ${dove}.`
                    : `Per l’allenamento di ${dove} il maestro è ancora da assegnare: appena c’è, te lo dico.`;
            }
            return p.maestro
                ? `La lezione la tiene ${p.maestro}: ${dove}.`
                : `Per la lezione di ${dove} il maestro è ancora da assegnare: appena c’è, te lo dico.`;
        case 'formazione': {
            const pezzi = [];
            if (p.entrati?.length)
                pezzi.push(`entra ${p.entrati.join(', ')}`);
            if (p.usciti?.length)
                pezzi.push(`esce ${p.usciti.join(', ')}`);
            const cambio = pezzi.length > 0 ? ` (${pezzi.join('; ')})` : '';
            return `È cambiata la formazione${cambio}: ${dove}.`;
        }
    }
}
/**
 * Ciò che il ponte consegna al bot, e ciò che il consegnatore manda a Telegram:
 * **la stessa frase**, dalla stessa riga.
 */
export const avvisoDaRaccontareSchema = z.object({
    id: z.uuid(),
    memberId: z.uuid(),
    /**
     * 🔄 La chat che **il gestionale** conosce per quel socio, se ne conosce una.
     *
     * ⚖️ **Nullabile dal 23/09/2026**, e la ragione è di chi sa cosa: la chat di
     * un socio nel bot la sa **il bot** — è lui che l'ha ammesso, e tiene le sue
     * abilitazioni. La rubrica del gestionale (`telegram_contacts`) serve al
     * consegnatore interno, e nel mondo nuovo nessuno la riempie dal bot. Col
     * filtro di prima, ogni avviso di un socio che il gestionale non aveva in
     * rubrica finiva `skipped` **prima** di arrivare a chi sapeva dove mandarlo.
     * ⇒ Il bot usa la sua; se non ce l'ha nemmeno lui, risponde `rifiutato` e la
     *    riga resta scritta «non consegnato», che la segreteria vede.
     */
    chatId: z.string().nullable(),
    gesture: gestoSchema,
    text: z.string(),
    bookingId: z.uuid().nullable(),
    /** 🌙 Solo sul gesto `caparra` da pagare: il bot mette sotto il bottone, e rilegge la caparra al tocco. */
    depositId: z.uuid().nullable().optional(),
    /** 🔁 Fase 3 (R7) — la caparra è il rinnovo del campo fisso: niente «Annulla» sotto. */
    depositRenewal: z.boolean().optional(),
    /** 🆕 27/09/2026 — il campo vuoto: sotto l'avviso «🎾 Prenota un campo». */
    bookAgain: z.boolean().optional(),
    /**
     * 🆕 Voce 50 — il campo vuoto: la partita da ricontrollare, per «✉️ Eravamo in campo». Al tocco il
     * bot rilegge la partita (`/bookings/:id`) e prepara il messaggio per la Segreteria. Sul rinnovo del
     * fisso non è `bookingId`, che lì è la partita dell'acconto nuovo.
     */
    recheckBookingId: z.uuid().nullable().optional(),
    /** 🆕 Voce 50 — il blocco appena scattato: sotto l'avviso «✉️ Scrivi alla Segreteria». */
    writeToDesk: z.boolean().optional(),
    /**
     * 🔁 27/09/2026 (voce 47) — sul gesto `fisso`: la serie da rinnovare, e da dove si rinnova. Il bot
     * mette il bottone e, al tocco, rilegge il rinnovo (`/players/:id/recurring/:seriesId/renewal`).
     */
    seriesId: z.uuid().nullable().optional(),
    seriesRenewal: z.enum(['bot', 'segreteria']).nullable().optional(),
    /**
     * 🔁 Fase 3 (R5) — sul gesto `organizzatore`: la richiesta a cui rispondere (con la caparra da
     * versare, e se prende il posto di chi chiede), o la richiesta finita senza cambio, per il
     * bottone «Chiedo a un altro».
     */
    changeId: z.uuid().nullable().optional(),
    changeWithDeposit: z.boolean().optional(),
    changeTakesPlace: z.boolean().optional(),
    changeEnded: z.uuid().nullable().optional(),
    /** ✉️ Sul gesto `risposta`: il messaggio del socio a cui si risponde, per «✉️ Rispondi alla Segreteria». */
    feedbackId: z.uuid().nullable().optional(),
    createdAt: z.string(),
});
export const elencoDaRaccontareSchema = z.object({ items: z.array(avvisoDaRaccontareSchema) });
/**
 * ⛔ **TRE VERDETTI_DEL_PONTE E SOLO TRE** (§ 02.7) — e sono gli stessi tre di tutto il
 * ponte, non tre parole nuove per questa rotta. Il terzo non è un lusso:
 * quando non si sa com'è andata, la risposta è «non lo so», mai una
 * supposizione.
 *
 * ⚖️ E qui i tre verdetti fanno anche da **politica di ritentativo**, che è il
 * motivo per cui val la pena che siano tre e non due:
 *   · `fatto`      → la riga è consegnata, e non si tocca più;
 *   · `rifiutato`  → un no **definitivo** (il socio ha bloccato il bot, la chat
 *                    non esiste): ritentare non cambierà niente, e si smette;
 *   · `non_lo_so`  → un intoppo **passeggero**: si riprova più tardi.
 * 📌 *Un rifiuto definitivo e un intoppo passeggero trattati allo stesso modo
 * fanno due danni opposti: si insiste dove è inutile e si molla dove bastava
 * aspettare.*
 */
/**
 * ⚠️ Si chiama «del ponte» e non «verdetto» perché `Verdetto` c'è già, ed è
 * un'altra cosa: quello della **griglia** (`griglia.ts`), che dice se uno slot
 * si può prenotare. Due parole uguali per due domande diverse sono la strada
 * più corta per applicare la risposta dell'una all'altra.
 */
export const VERDETTI_DEL_PONTE = ['fatto', 'rifiutato', 'non_lo_so'];
export const verdettoDelPonteSchema = z.enum(VERDETTI_DEL_PONTE);
export const esitoRaccontoSchema = z.object({
    outcome: verdettoDelPonteSchema,
    /** Per un socio, non per un tecnico: qui dentro non entra nessun nome interno. */
    reason: z.string().max(500).optional(),
});
//# sourceMappingURL=avvisi.js.map